"use client";
import { motion } from "framer-motion";
import { itemVariants } from "@/lib/animation-variants";
import React from "react";

interface MarkdownBoxProps {
  markdown: string;
  className?: string;
}

// Export a generic markdown renderer
export function renderMarkdown(md: string) {
  const linkRegex = /\[([^\]]+)\]\(([^\)]+)\)/g;
  const boldRegex = /\*\*([^*]+)\*\*/g;
  const italicRegex = /\*([^*]+)\*/g;
  const lines = md.split("\n");
  const out = [];
  let currentList: string[] = [];
  let keyIndex = 0;
  
  function renderInlineMarkdown(text: string, keyPrefix = ""): React.ReactNode[] {
    const parts: React.ReactNode[] = [];
    let processedText = text;
    let partKey = 0;
    
    // First, process links
    const linkMatches: Array<{ index: number; length: number; label: string; url: string }> = [];
    linkRegex.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = linkRegex.exec(processedText)) !== null) {
      linkMatches.push({
        index: match.index,
        length: match[0].length,
        label: match[1],
        url: match[2],
      });
    }
    
    // Then process bold
    const boldMatches: Array<{ index: number; length: number; content: string }> = [];
    boldRegex.lastIndex = 0;
    while ((match = boldRegex.exec(processedText)) !== null) {
      boldMatches.push({
        index: match.index,
        length: match[0].length,
        content: match[1],
      });
    }
    
    // Then process italic (but not if it's part of bold)
    const italicMatches: Array<{ index: number; length: number; content: string }> = [];
    italicRegex.lastIndex = 0;
    while ((match = italicRegex.exec(processedText)) !== null) {
      const italicMatch = match; // Store in local variable for type narrowing
      // Check if this italic is inside a bold match
      const isInsideBold = boldMatches.some(
        (bm) => italicMatch.index >= bm.index && italicMatch.index < bm.index + bm.length
      );
      if (!isInsideBold) {
        italicMatches.push({
          index: italicMatch.index,
          length: italicMatch[0].length,
          content: italicMatch[1],
        });
      }
    }
    
    // Combine all matches and sort by index
    const allMatches: Array<{
      index: number;
      length: number;
      type: "link" | "bold" | "italic";
      content?: string;
      label?: string;
      url?: string;
    }> = [
      ...linkMatches.map((m) => ({ ...m, type: "link" as const, label: m.label, url: m.url })),
      ...boldMatches.map((m) => ({ ...m, type: "bold" as const, content: m.content })),
      ...italicMatches.map((m) => ({ ...m, type: "italic" as const, content: m.content })),
    ].sort((a, b) => a.index - b.index);
    
    let lastIndex = 0;
    for (const match of allMatches) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(processedText.slice(lastIndex, match.index));
      }
      
      // Add the matched element
      if (match.type === "link") {
        const isMailto = match.url!.startsWith("mailto:");
        const isExt = !isMailto && /^https?:/i.test(match.url!);
        parts.push(
          <a
            key={`${keyPrefix}-link-${partKey++}`}
            href={match.url}
            className="break-all text-gothia-gold underline decoration-gothia-gold underline-offset-2 transition hover:text-gothia-gold-dark"
            {...(isExt ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
            {match.label}
          </a>
        );
      } else if (match.type === "bold") {
        parts.push(
          <strong key={`${keyPrefix}-bold-${partKey++}`}>
            {match.content}
          </strong>
        );
      } else if (match.type === "italic") {
        parts.push(
          <em key={`${keyPrefix}-italic-${partKey++}`}>
            {match.content}
          </em>
        );
      }
      
      lastIndex = match.index + match.length;
    }
    
    // Add remaining text
    if (lastIndex < processedText.length) {
      parts.push(processedText.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  }
  
  function renderWithLinks(text: string, keyPrefix = "") {
    const rendered = renderInlineMarkdown(text, keyPrefix);
    return rendered.length === 1 && typeof rendered[0] === "string" ? rendered[0] : rendered;
  }
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    if (/^# (.*)/.test(line)) {
      out.push(
        <h1
          className="text-headline mb-5 mt-2 text-gothia-gold"
          key={"h1" + i}>
          {renderWithLinks(line.replace(/^# /, ""), `h1-${i}`)}
        </h1>,
      );
    } else if (/^## (.*)/.test(line)) {
      out.push(
        <h2
          className="text-section-title mb-3 mt-6 text-gothia-green"
          key={"h2" + i}>
          {renderWithLinks(line.replace(/^## /, ""), `h2-${i}`)}
        </h2>,
      );
    } else if (/^### (.*)/.test(line)) {
      out.push(
        <div
          className="text-subheading mb-2 mt-4 text-gothia-green"
          key={"h3" + i}>
          {renderWithLinks(line.replace(/^### /, ""), `h3-${i}`)}
        </div>,
      );
    } else if (/^[-*]{3,}$/.test(line.trim())) {
      out.push(
        <hr
          className="my-6 border-t border-gothia-gold/30"
          key={"hr" + i}
        />
      );
    } else if (/^\s*- /.test(line)) {
      currentList.push(line.replace(/^\s*- /, ""));
      if (!/^\s*- /.test(lines[i + 1] || "")) {
        out.push(
          <ul
            className="mb-2 list-disc space-y-0.5 pl-5"
            key={"ul" + keyIndex++}>
            {currentList.map((li, j) => (
              <li className="text-body text-muted-foreground" key={j}>
                {renderWithLinks(li, `li-${i}-${j}`)}
              </li>
            ))}
          </ul>,
        );
        currentList = [];
      }
    } else if (line.trim() === "") {
      out.push(<div className="h-2" key={"sp" + i}></div>);
    } else {
      out.push(
        <p className="mb-2 text-body text-muted-foreground" key={"p" + i}>
          {renderWithLinks(line, `p-${i}`)}
        </p>,
      );
    }
  }
  return out;
}

const MarkdownBox: React.FC<MarkdownBoxProps> = ({ markdown, className }) => (
  <motion.div
    variants={itemVariants}
    className={`rounded-lg border border-gothia-gold/30 bg-gothia-cream p-6 backdrop-blur-sm ${className ?? ""}`}>
    {renderMarkdown(markdown)}
  </motion.div>
);

export default MarkdownBox;
